import React, { useState } from 'react'
import { useToast } from '../../../contexts/ToastContext'

// Секции
import SalaryAccordion from './salary/SalaryAccordion'
import BasicRatesSection from './salary/BasicRatesSection'
import PayoutScheduleSection from './salary/PayoutScheduleSection'
import HelperSection from './salary/HelperSection'
import CustomRulesSection from './salary/CustomRulesSection'
import AdvancedSection from './salary/AdvancedSection'
import SalaryFormulaSummary from './salary/SalaryFormulaSummary'

// Модалки
import ConvertModal from './salary/ConvertModal'
import EffectiveDateModal from './salary/EffectiveDateModal'

// ─────────────────────────────────────────────
// Иконки для аккордеонов
// ─────────────────────────────────────────────
const ICONS = {
  rates: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  schedule: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  helper: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  custom: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
    </svg>
  ),
  advanced: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
}

// ─────────────────────────────────────────────
// Хелперы для превью
// ─────────────────────────────────────────────
function getRatesPreview(s) {
  const parts = []
  if (s.hourlyRate > 0) parts.push(`${s.hourlyRate}₽/ч`)
  else if (s.dailyRate > 0) parts.push(`${s.dailyRate}₽/см`)
  if (s.bonusPerAccepted > 0) parts.push(`${s.bonusPerAccepted}₽/пр`)
  if (s.bonusPerIssued > 0) parts.push(`${s.bonusPerIssued}₽/выд`)
  if (s.turnoverPercent > 0) parts.push(`${s.turnoverPercent}%`)
  return parts.length > 0 ? parts.join(' · ') : 'Не настроено'
}

function getSchedulePreview(s) {
  const labels = {
    bi_monthly: '2 раза в месяц',
    biweekly: '2 раза в месяц',
    weekly: 'Еженедельно',
    monthly: 'Ежемесячно',
    shifts: `Каждые ${s.payoutShiftsCount || 10} смен`,
    daily: 'Ежедневно',
  }
  const dayNames = ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье']
  const rawFreq = s.payoutFrequency || 'weekly'
  const freq = rawFreq === 'biweekly' ? 'bi_monthly' : rawFreq
  let preview = labels[freq] || freq

  if ((freq === 'weekly' || freq === 'bi_monthly' || freq === 'biweekly') && s.payoutDay) {
    preview += ` · ${dayNames[s.payoutDay] || ''}`
  } else if (freq === 'monthly' && s.payoutDay) {
    preview += ` · ${s.payoutDay}-го`
  }
  return preview
}

function getHelperPreview(s) {
  if (s.enableHelpers === false) return 'Отключён'
  const parts = ['Включён']
  if (s.helperHourlyRate > 0) parts.push(`${s.helperHourlyRate}₽/ч`)
  if (s.helperDeductionPercent > 0) parts.push(`−${s.helperDeductionPercent}%`)
  return parts.join(' · ')
}

function getCustomPreview(s) {
  const count = s.customRules?.length || 0
  if (count === 0) return 'Нет правил'
  const word = count === 1 ? 'правило' : count < 5 ? 'правила' : 'правил'
  return `${count} ${word}`
}

function getAdvancedPreview(s) {
  const parts = []
  if (s.bonusCalculationMethod === 'collective_pool_split') parts.push('Коллективный')
  else parts.push('Индивидуальный')
  if (s.dailyRateMode === 'manual') parts.push('Ручной оклад')
  return parts.join(' · ')
}

// ─────────────────────────────────────────────
// Компонент
// ─────────────────────────────────────────────
export default function SalarySettings({
  salaryLoading,
  salarySettings,
  setSalarySettings,
  salaryDirty,
  setSalaryDirty,
  salarySaving,
  setSalarySaving,
  handleSaveSalarySettings,
  profile
}) {
  const toast = useToast()

  // Модалка конвертации
  const [convertRule, setConvertRule] = useState(null)
  // Модалка даты
  const [showDateModal, setShowDateModal] = useState(false)

  // ─────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────
  const handleSalaryChange = (field, value) => {
    setSalarySettings({ ...salarySettings, [field]: value })
    setSalaryDirty(true)
  }

  const handleAddCustomRule = (ruleType = 'per_item') => {
    let newRule = {
      id: Date.now().toString(),
      name: '',
      amount: '',
      type: 'per_item'
    }
    if (ruleType === 'volume_bonus') {
      newRule = {
        id: 'system:vol_bonus_' + Date.now(),
        name: 'Бонус за объем',
        type: 'volume_bonus',
        metric: 'accepted',
        threshold: 1000,
        amount: 1000,
        active: true
      }
    }
    setSalarySettings(prev => ({
      ...prev,
      customRules: [...(prev.customRules || []), newRule]
    }))
    setSalaryDirty(true)
  }

  const handleUpdateCustomRule = (id, field, value) => {
    setSalarySettings(prev => ({
      ...prev,
      customRules: prev.customRules.map(r => r.id === id ? { ...r, [field]: value } : r)
    }))
    setSalaryDirty(true)
  }

  const handleRemoveCustomRule = (id) => {
    setSalarySettings(prev => ({
      ...prev,
      customRules: prev.customRules.filter(r => r.id !== id)
    }))
    setSalaryDirty(true)
  }

  const handleOpenConvertModal = (rule) => {
    setConvertRule({
      ruleId: rule.id,
      ruleName: rule.name || 'Без названия',
      ruleAmount: rule.amount,
      ruleType: rule.type,
    })
  }

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className="settings-section animate-fade-in">
      {salaryLoading ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div className="empty-state-title">Загрузка настроек...</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* ═══ 1. Оплата за работу ═══ */}
          <SalaryAccordion
            icon={ICONS.rates}
            title="Оплата за работу"
            subtitle="Ставки за смену, бонусы за приёмку, выдачу, возврат"
            preview={getRatesPreview(salarySettings)}
            defaultOpen={false}
            accentColor="#3B82F6"
            dirty={salaryDirty}
            data-tour="salary-rates-card"
          >
            <BasicRatesSection settings={salarySettings} onChange={handleSalaryChange} />
          </SalaryAccordion>

          {/* ═══ 2. График выплат ═══ */}
          <SalaryAccordion
            icon={ICONS.schedule}
            title="График выплат"
            subtitle="Когда и как часто платить"
            preview={getSchedulePreview(salarySettings)}
            accentColor="#8B5CF6"
            dirty={salaryDirty}
          >
            <PayoutScheduleSection settings={salarySettings} onChange={handleSalaryChange} />
          </SalaryAccordion>

          {/* ═══ 3. Помощник ═══ */}
          <SalaryAccordion
            icon={ICONS.helper}
            title="Помощник"
            subtitle="Тарифы помощника, влияние на основного сотрудника"
            preview={getHelperPreview(salarySettings)}
            accentColor="#10B981"
            statusDot={salarySettings.enableHelpers !== false ? 'green' : 'gray'}
            statusDotTooltip={salarySettings.enableHelpers !== false ? 'Помощники включены' : 'Помощники выключены'}
            dirty={salaryDirty}
          >
            <HelperSection settings={salarySettings} onChange={handleSalaryChange} />
          </SalaryAccordion>

          {/* ═══ 4. Доп. начисления ═══ */}
          <SalaryAccordion
            icon={ICONS.custom}
            title="Доп. начисления"
            subtitle="Кастомные правила оплаты"
            preview={getCustomPreview(salarySettings)}
            accentColor="#F59E0B"
            dirty={salaryDirty}
          >
            <CustomRulesSection
              settings={salarySettings}
              onChange={handleSalaryChange}
              onAddRule={handleAddCustomRule}
              onUpdateRule={handleUpdateCustomRule}
              onRemoveRule={handleRemoveCustomRule}
              onConvertRule={handleOpenConvertModal}
            />
          </SalaryAccordion>

          {/* ═══ 5. Дополнительно ═══ */}
          <SalaryAccordion
            icon={ICONS.advanced}
            title="Дополнительно"
            subtitle="Режимы расчёта и отображение"
            preview={getAdvancedPreview(salarySettings)}
            accentColor="#6366F1"
            dirty={salaryDirty}
          >
            <AdvancedSection settings={salarySettings} onChange={handleSalaryChange} />
          </SalaryAccordion>

          {/* ═══ Формула расчёта (всегда видна) ═══ */}
          <SalaryFormulaSummary settings={salarySettings} />

          {/* ═══ Кнопка сохранения ═══ */}
          <div style={{
            position: 'sticky', bottom: '16px', zIndex: 10,
            padding: '16px', marginTop: '4px',
            background: salaryDirty
              ? 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.08))'
              : 'var(--surface-color)',
            borderRadius: '16px',
            border: salaryDirty
              ? '1.5px solid color-mix(in srgb, var(--wb-primary) 25%, var(--border-color))'
              : '1.5px solid var(--border-color)',
            backdropFilter: 'blur(12px)',
            boxShadow: salaryDirty
              ? '0 -4px 24px rgba(59,130,246,0.12), 0 4px 12px rgba(0,0,0,0.06)'
              : '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'all 0.3s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
          }}>
            {salaryDirty ? (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--warning, #F59E0B)',
                  animation: 'pulse 2s infinite',
                  flexShrink: 0,
                }} />
                Есть несохранённые изменения
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success, #10B981)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Все изменения сохранены
              </div>
            )}
            <button
              type="button"
              className={`btn ${salaryDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowDateModal(true)}
              disabled={!salaryDirty || salarySaving}
              style={{
                minWidth: '180px',
                ...(salaryDirty ? {
                  background: 'linear-gradient(135deg, var(--wb-primary), #9333ea)',
                  boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                } : {}),
              }}
            >
              {salarySaving ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                  {' '}Сохранение...
                </>
              ) : salaryDirty ? 'Сохранить изменения' : 'Сохранено'}
            </button>
          </div>
        </div>
      )}

      {/* ═══ Модалка конвертации ═══ */}
      <ConvertModal
        rule={convertRule}
        onClose={() => setConvertRule(null)}
        onConverted={() => setConvertRule(null)}
        salarySettings={salarySettings}
        setSalarySettings={setSalarySettings}
        setSalaryDirty={setSalaryDirty}
        profileId={profile?.id}
      />

      {/* ═══ Модалка даты ═══ */}
      <EffectiveDateModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        onSave={handleSaveSalarySettings}
        saving={salarySaving}
      />
    </div>
  )
}
